import React, { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Star, Copy, Trash2, Plus, Save, Check, Lock, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { RIGHT_NOW_SECTIONS, ALL_PRESET_ID } from "@/lib/hqPrefs";

/**
 * ManagePresetsDialog — full CRUD for Right Now preset library.
 *
 * The dialog receives the current preset library, current visible/collapsed
 * state, and a single async `onSave(nextLibrary)` handler that persists the
 * whole document via PUT. All local edits happen against a `draft` copy so
 * users can cancel at any time. The locked "All" preset is guarded — its
 * name/description/layout cannot be edited and it cannot be deleted.
 *
 * `applyingPresetId` is passed back to the parent when the caller taps
 * "Apply" on a row so the drawer preview updates without closing the dialog.
 */
export default function ManagePresetsDialog({
  open,
  onOpenChange,
  presets = [],
  defaultPresetId,
  currentPresetId,
  hiddenSections = [],
  collapsedSections = [],
  onSave,
  onApply,
}) {
  const [draft, setDraft] = useState({ presets: [], defaultId: null });
  const [selectedId, setSelectedId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newPresetOpen, setNewPresetOpen] = useState(false);

  // Reset local draft every time the dialog opens.
  React.useEffect(() => {
    if (open) {
      setDraft({
        presets: (presets || []).map((p) => ({ ...p })),
        defaultId: defaultPresetId || (presets[0] && presets[0].id) || null,
      });
      setSelectedId(currentPresetId || defaultPresetId || (presets[0] && presets[0].id) || null);
      setNewPresetOpen(false);
      setDeleteId(null);
    }
  }, [open, presets, defaultPresetId, currentPresetId]);

  const selected = draft.presets.find((p) => p.id === selectedId) || null;
  const canEdit = selected && !selected.is_locked;

  const updateSelected = (patch) => {
    setDraft((d) => ({
      ...d,
      presets: d.presets.map((p) => p.id === selectedId ? { ...p, ...patch } : p),
    }));
  };

  const toggleHiddenLocal = (key) => {
    if (!selected || selected.is_locked) return;
    const set = new Set(selected.hiddenSections || []);
    if (set.has(key)) set.delete(key); else set.add(key);
    updateSelected({ hiddenSections: Array.from(set) });
  };
  const toggleCollapsedLocal = (key) => {
    if (!selected || selected.is_locked) return;
    const set = new Set(selected.collapsedSections || []);
    if (set.has(key)) set.delete(key); else set.add(key);
    updateSelected({ collapsedSections: Array.from(set) });
  };

  const duplicatePreset = (p) => {
    const copy = {
      id: `preset_${Math.random().toString(36).slice(2, 10)}`,
      name: `${p.name} (Copy)`,
      description: p.description || "",
      hiddenSections: [...(p.hiddenSections || [])],
      collapsedSections: [...(p.collapsedSections || [])],
      is_locked: false,
    };
    setDraft((d) => ({ ...d, presets: [...d.presets, copy] }));
    setSelectedId(copy.id);
  };

  const addFromCurrent = (name, description) => {
    const copy = {
      id: `preset_${Math.random().toString(36).slice(2, 10)}`,
      name: name.trim() || "Untitled preset",
      description: (description || "").trim(),
      hiddenSections: [...hiddenSections],
      collapsedSections: [...collapsedSections],
      is_locked: false,
    };
    setDraft((d) => ({ ...d, presets: [...d.presets, copy] }));
    setSelectedId(copy.id);
    setNewPresetOpen(false);
  };

  const confirmDelete = () => {
    const id = deleteId;
    setDeleteId(null);
    if (!id) return;
    setDraft((d) => {
      const nextPresets = d.presets.filter((p) => p.id !== id);
      const nextDefault = d.defaultId === id ? (nextPresets[0] && nextPresets[0].id) : d.defaultId;
      return { presets: nextPresets, defaultId: nextDefault };
    });
    if (selectedId === id) setSelectedId(null);
  };

  const setDefault = (id) => setDraft((d) => ({ ...d, defaultId: id }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        presets: draft.presets,
        default_preset_id: draft.defaultId,
        last_selected_preset_id: currentPresetId,
      });
      toast.success("Presets saved");
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not save presets");
    } finally {
      setSaving(false);
    }
  };

  const hiddenSet = useMemo(() => new Set(selected?.hiddenSections || []), [selected]);
  const collapsedSet = useMemo(() => new Set(selected?.collapsedSections || []), [selected]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl bg-[#0F131B] border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col p-0"
          data-testid="rn-preset-manage-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/5">
            <DialogTitle className="font-display text-xl">Manage Right Now presets</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">
              Presets only control which categories are visible and collapsed. They never change the
              underlying data.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-0">
            {/* Preset list */}
            <div className="border-b md:border-b-0 md:border-r border-white/5 overflow-y-auto max-h-[45vh] md:max-h-none">
              <div className="p-3 flex items-center justify-between gap-2 sticky top-0 bg-[#0F131B] z-10 border-b border-white/5">
                <span className="text-eyebrow">Presets ({draft.presets.length})</span>
                <button
                  type="button"
                  onClick={() => setNewPresetOpen(true)}
                  data-testid="rn-preset-new-btn"
                  className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> New
                </button>
              </div>
              <div className="p-2 space-y-1">
                {draft.presets.map((p) => (
                  <div
                    key={p.id}
                    className={`group rounded-md border transition-colors cursor-pointer ${
                      selectedId === p.id
                        ? "border-white/20 bg-white/[0.06]"
                        : "border-transparent hover:bg-white/[0.04]"
                    }`}
                    onClick={() => setSelectedId(p.id)}
                    data-testid={`rn-preset-row-${p.id}`}
                  >
                    <div className="p-2.5 flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm truncate">{p.name}</span>
                          {p.id === draft.defaultId && (
                            <Star className="w-3 h-3 text-amber-300 flex-shrink-0" title="Default" />
                          )}
                          {p.is_locked && (
                            <Lock className="w-3 h-3 text-white/40 flex-shrink-0" title="Locked" />
                          )}
                        </div>
                        {p.description && (
                          <div className="text-[11px] text-white/40 truncate mt-0.5">{p.description}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onApply && onApply(p); }}
                        data-testid={`rn-preset-apply-${p.id}`}
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/10 text-white/60 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Apply now"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detail editor */}
            <div className="overflow-y-auto p-5 min-h-0">
              {!selected && (
                <div className="text-sm text-white/50 py-16 text-center">
                  Select a preset to edit, or tap New to create one.
                </div>
              )}
              {selected && (
                <div className="space-y-5" data-testid="rn-preset-editor">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-eyebrow">Preset details</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setDefault(selected.id)}
                        disabled={draft.defaultId === selected.id}
                        data-testid="rn-preset-set-default-btn"
                        className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Star className="w-3 h-3" />
                        {draft.defaultId === selected.id ? "Default" : "Set default"}
                      </button>
                      <button
                        type="button"
                        onClick={() => duplicatePreset(selected)}
                        data-testid="rn-preset-duplicate-btn"
                        className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Duplicate
                      </button>
                      {!selected.is_locked && (
                        <button
                          type="button"
                          onClick={() => setDeleteId(selected.id)}
                          data-testid="rn-preset-delete-btn"
                          className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-red-500/30 bg-red-500/10 text-red-300 hover:text-white hover:bg-red-500/20 transition-colors inline-flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-eyebrow flex items-center gap-1"><Pencil className="w-3 h-3" /> Name</label>
                    <Input
                      value={selected.name}
                      onChange={(e) => updateSelected({ name: e.target.value })}
                      disabled={!canEdit}
                      maxLength={80}
                      data-testid="rn-preset-name-input"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-eyebrow">Description</label>
                    <Textarea
                      value={selected.description || ""}
                      onChange={(e) => updateSelected({ description: e.target.value })}
                      disabled={!canEdit}
                      maxLength={280}
                      rows={2}
                      data-testid="rn-preset-desc-input"
                      className="bg-white/5 border-white/10 text-white resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="text-eyebrow">Sections</div>
                    <div className="rounded-md border border-white/10 bg-white/[0.03] overflow-hidden">
                      {RIGHT_NOW_SECTIONS.map((s, idx) => {
                        const hidden = hiddenSet.has(s.key);
                        const collapsed = collapsedSet.has(s.key);
                        return (
                          <div
                            key={s.key}
                            className={`flex items-center justify-between gap-2 px-3 py-2 ${idx > 0 ? "border-t border-white/5" : ""}`}
                            data-testid={`rn-preset-section-row-${s.key}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white/85 truncate">{s.label}</div>
                              <div className="text-[11px] text-white/40">
                                {hidden ? "Hidden" : collapsed ? "Collapsed" : "Visible"}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => toggleHiddenLocal(s.key)}
                                disabled={!canEdit}
                                data-testid={`rn-preset-toggle-hide-${s.key}`}
                                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border transition-colors ${
                                  hidden
                                    ? "border-white/25 bg-white/10 text-white"
                                    : "border-white/10 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08]"
                                } disabled:opacity-40 disabled:cursor-not-allowed`}
                              >
                                {hidden ? "Hidden" : "Hide"}
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleCollapsedLocal(s.key)}
                                disabled={!canEdit || hidden}
                                data-testid={`rn-preset-toggle-collapse-${s.key}`}
                                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md border transition-colors ${
                                  collapsed
                                    ? "border-white/25 bg-white/10 text-white"
                                    : "border-white/10 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08]"
                                } disabled:opacity-40 disabled:cursor-not-allowed`}
                              >
                                {collapsed ? "Collapsed" : "Collapse"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {selected.id === ALL_PRESET_ID && (
                      <div className="text-[11px] text-white/40 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> The All preset is locked as a safe reset.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-white/5 flex items-center gap-2 flex-wrap">
            <div className="text-[11px] text-white/40 flex-1 min-w-[120px]">
              Changes save when you tap Save.
            </div>
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="rn-preset-cancel-btn"
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="rn-preset-save-btn"
              className="rounded-full bg-white text-black hover:bg-white/90"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create-from-current dialog */}
      <NewPresetDialog
        open={newPresetOpen}
        onOpenChange={setNewPresetOpen}
        onCreate={addFromCurrent}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0F131B] border-white/10 text-white" data-testid="rn-preset-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this preset?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              This only removes the saved layout — no underlying data is affected. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-white/10 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="rn-preset-delete-confirm-btn"
              className="bg-red-500/80 hover:bg-red-500 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function NewPresetDialog({ open, onOpenChange, onCreate }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  React.useEffect(() => {
    if (open) { setName(""); setDescription(""); }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#0F131B] border-white/10 text-white" data-testid="rn-preset-new-dialog">
        <DialogHeader>
          <DialogTitle>Save current layout as preset</DialogTitle>
          <DialogDescription className="text-white/50">
            Captures the sections currently visible and collapsed on Right Now.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-eyebrow">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Monday review"
              autoFocus
              maxLength={80}
              data-testid="rn-preset-new-name"
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-eyebrow">Description <span className="text-white/40 normal-case">(optional)</span></label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={280}
              data-testid="rn-preset-new-desc"
              className="bg-white/5 border-white/10 text-white resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onCreate(name, description)}
            disabled={!name.trim()}
            data-testid="rn-preset-new-create-btn"
            className="rounded-full bg-white text-black hover:bg-white/90"
          >
            <Check className="w-4 h-4 mr-1.5" /> Save preset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
